import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { subscribeToRoom } from '@db/roomService';
import { submitAnswer } from '@db/submissionService';
import { getCurrentUid, ensureAnonymousAuth } from '@db/firebase';
import type { ShortAnswerConfig } from '@types-local/quiz.types';
import type { Room } from '@types-local/room.types';

export default function AnswerShortText(): React.ReactElement {
  const [room, setRoom] = useState<Room | null>(null);
  const [text, setText] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const studentName = sessionStorage.getItem('gh_student_name') ?? 'Student';
  const roomCode = sessionStorage.getItem('gh_room_code') ?? '';
  const classId = sessionStorage.getItem('gh_class_id') ?? '';

  useEffect(() => {
    ensureAnonymousAuth();
    if (!roomCode) return;
    return subscribeToRoom(roomCode, setRoom);
  }, [roomCode]);

  const config = room?.activeConfig?.type === 'short_answer'
    ? (room.activeConfig as ShortAnswerConfig) : null;

  // Reset for each new question so students flow through the session.
  useEffect(() => {
    setText('');
    setSubmitted(false);
  }, [config?.question]);

  const handleSubmit = async () => {
    if (!text.trim() || !room?.activeSlideId) return;
    setSubmitting(true);
    try {
      const uid = getCurrentUid() ?? 'anon';
      await submitAnswer(roomCode, uid, studentName, room.activeSlideId, {
        type: 'short_answer', text: text.trim(),
      }, classId);
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center p-6">
        <div className="space-y-3"><div className="text-5xl">✏️</div><h2 className="text-xl font-bold text-slate-900">Waiting for activity…</h2></div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center p-6">
        <div className="space-y-4">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-6xl">✅</motion.div>
          <h2 className="text-xl font-bold text-slate-900">Answer Submitted!</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col p-4 max-w-lg mx-auto">
      <div className="glass rounded-3xl p-5 mb-4">
        <p className="text-xs text-green-400 font-semibold mb-2">✏️ Short Answer</p>
        <h2 className="text-base font-bold text-slate-900">{config.question}</h2>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, config.maxLength))}
        placeholder="Type your answer here…"
        rows={5}
        className="w-full bg-surface-elevated border border-surface-border rounded-2xl px-4 py-3 text-base text-slate-900 placeholder-slate-400 resize-none focus:outline-none focus:border-green-500 transition-colors mb-2"
      />
      <p className="text-xs text-slate-400 text-right mb-4">{text.length}/{config.maxLength}</p>
      <motion.button
        onClick={handleSubmit}
        disabled={!text.trim() || submitting}
        whileTap={{ scale: 0.97 }}
        className="w-full py-4 rounded-2xl bg-gradient-to-r from-green-600 to-green-500 text-white font-bold text-base disabled:opacity-60"
      >
        {submitting ? 'Submitting…' : '🚀 Submit Answer'}
      </motion.button>
    </div>
  );
}
